import { useLayoutEffect, useRef, type RefObject } from 'react';
import type { BoardLayout } from '../lib/layout';
import {
  boardChanges,
  frameOf,
  type BoardFrame,
  type BoardSnapshot,
  type Rect,
} from '../lib/motion';

/** Delay between tiles as a board arrives, biggest first (ms). */
const ARRIVE_STEP_MS = 55;

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Plays a `data-motion` animation on an element for `ms` (restarting it if it's running). Data
 * attributes rather than classes, so React re-rendering the class names can't cut it short.
 */
function play(el: HTMLElement, motion: string, ms: number) {
  el.removeAttribute('data-motion');
  void el.offsetWidth; // restart the animation
  el.dataset.motion = motion;
  window.setTimeout(() => {
    if (el.dataset.motion === motion) el.removeAttribute('data-motion');
  }, ms);
}

/** A copy of a tile that left the board, fading where it was. */
function leave(board: HTMLElement, tile: HTMLElement, from: Rect) {
  const ghost = tile.cloneNode(true) as HTMLElement;
  for (const a of ['data-post-id', 'data-motion', 'title', 'aria-label']) ghost.removeAttribute(a);
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  ghost.classList.add('tile--leaving');
  Object.assign(ghost.style, {
    translate: `${from.x}px ${from.y}px`,
    width: `${from.w}px`,
    height: `${from.h}px`,
  });
  board.append(ghost);
  void ghost.offsetWidth; // start visible, then fade
  ghost.style.opacity = '0';
  window.setTimeout(() => ghost.remove(), 700);
}

/** A new view: posts land one after another, biggest first, then the talk of the town's badge. */
function arrive(board: HTMLElement, frame: BoardFrame, tiles: ReadonlyMap<string, HTMLElement>) {
  const order = [...frame.rects].sort((a, b) => b[1].w * b[1].h - a[1].w * a[1].h);
  order.forEach(([id], i) => tiles.get(id)?.style.setProperty('--order', String(i)));
  board.style.setProperty('--arrivals', String(order.length));
  play(board, 'arriving', order.length * ARRIVE_STEP_MS + 1000);
}

/** After a refresh: new posts pop in, rising posts say so, leaving posts fade. */
function showChanges(
  board: HTMLElement,
  prev: { frame: BoardFrame; tiles: ReadonlyMap<string, HTMLElement> },
  frame: BoardFrame,
  tiles: ReadonlyMap<string, HTMLElement>,
) {
  const changes = boardChanges(prev.frame, frame);
  for (const id of changes.added) {
    const el = tiles.get(id);
    if (el) play(el, 'new', 2600);
  }
  for (const id of changes.rising) {
    const el = tiles.get(id);
    if (el) play(el, 'rising', 2800);
  }
  const crowned = changes.crowned && tiles.get(changes.crowned);
  if (crowned) play(crowned, 'crowned', 1800);
  for (const { id, from } of changes.left) {
    const el = prev.tiles.get(id);
    if (el) leave(board, el, from);
  }
}

/**
 * The board's motion: an arrival when a view loads, and on Refresh a look at what changed. It
 * plays once per snapshot, after the tiles are laid out (the board may still be measuring itself
 * when a snapshot is taken), and not at all for readers who ask for reduced motion.
 */
export function useBoardMotion(
  boardRef: RefObject<HTMLElement | null>,
  layout: BoardLayout,
  snapshot: BoardSnapshot,
): void {
  const last = useRef<{
    snapshot: number;
    frame: BoardFrame;
    tiles: Map<string, HTMLElement>;
  } | null>(null);
  const due = useRef<BoardSnapshot | null>(null);
  const { id, refreshed } = snapshot;

  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const frame = frameOf(layout, board.clientWidth, board.clientHeight);
    const tiles = new Map<string, HTMLElement>();
    for (const el of board.querySelectorAll<HTMLElement>('.tile[data-post-id]')) {
      tiles.set(el.dataset.postId!, el);
    }
    const prev = last.current;
    if (!prev || prev.snapshot !== id) due.current = { id, refreshed };
    const pending = due.current;
    if (pending && frame.rects.size > 0) {
      due.current = null;
      if (!reducedMotion()) {
        if (pending.refreshed && prev && prev.frame.rects.size > 0) {
          showChanges(board, prev, frame, tiles);
        } else arrive(board, frame, tiles);
      }
    }
    last.current = { snapshot: id, frame, tiles };
  }, [boardRef, layout, id, refreshed]);
}
